import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/authService';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      try {
        const currentUser = await authService.initialize();
        setUser(currentUser);
      } catch (error) {
        console.error('Session check failed:', error);
        // Clear invalid token
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for token invalidation fired by apiClient on 401 responses
    const handleLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Memoize callbacks to prevent unnecessary re-renders (React 18 best practice)
  const signIn = useCallback(async (email: string, password: string) => {
    const authResponse = await authService.signIn(email, password);
    // Construct User from AuthResponse
    setUser({
      id: authResponse.userId,
      email: authResponse.email,
      name: authResponse.name,
      createdAt: new Date().toISOString(),
    });
  }, []);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    const authResponse = await authService.signUp(email, password, name);
    // Construct User from AuthResponse
    setUser({
      id: authResponse.userId,
      email: authResponse.email,
      name: authResponse.name,
      createdAt: new Date().toISOString(),
    });
  }, []);

  const signOut = useCallback(async () => {
    await authService.signOut();
    setUser(null);
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({ user, loading, signIn, signUp, signOut }),
    [user, loading, signIn, signUp, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
